import React from 'react'
import { ValidationError, api } from '../api/client'
import type { ClientDetail, Consent } from '../types/app'
import { CONSENT_WORDING } from '../clients/wording'
import { resizeNotePhoto } from '../screens/settings/photoResize'
import { saveErrorMessage } from '../screens/settings/toast'
import { photoConsentStands } from './notePhotos'
import type { PickedPhoto } from './useNotePhotoQueue'

const BAD_FILE = 'Choose a JPEG or WebP photo under 2 MB.'

interface Props {
  client: ClientDetail
  // How many more photos fit (photosLeft); none left shows nothing.
  left: number
  businessName: string
  onPicked: (photo: PickedPhoto) => void
  // The new consent row the API returned, for the caller to put on the client.
  onConsentRecorded: (consent: Consent) => void
}

// AddNotePhoto is the way to add a treatment photo to an appointment note:
// Add photo over a file input (no `capture`, so a phone offers the camera and
// the library), or, while the client has not agreed to photos, a line saying
// so with a way to record her agreement in person.
export default function AddNotePhoto({ client, left, businessName, onPicked, onConsentRecorded }: Props) {
  const input = React.useRef<HTMLInputElement>(null)
  const [reading, setReading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [confirming, setConfirming] = React.useState(false)
  const [recording, setRecording] = React.useState(false)
  const [consentError, setConsentError] = React.useState('')
  // A resize or a consent call can finish after the sheet has moved on; its
  // answer then belongs to nobody.
  const alive = React.useRef(true)
  React.useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  if (left <= 0) return null

  const picked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Cleared so picking the same file again still counts as a change.
    e.target.value = ''
    if (!file) return
    setError('')
    setReading(true)
    try {
      const photo = await resizeNotePhoto(file)
      if (alive.current) onPicked(photo)
    } catch {
      if (alive.current) setError(BAD_FILE)
    } finally {
      if (alive.current) setReading(false)
    }
  }

  const record = async () => {
    setRecording(true)
    setConsentError('')
    try {
      const consent = await api.recordConsent(client.id, {
        kind: 'photos',
        granted: true,
        wording_version: CONSENT_WORDING.photos.version,
      })
      if (!alive.current) return
      onConsentRecorded(consent)
      setConfirming(false)
      // Straight on to the picker. A browser that wants a fresh tap for it
      // (Safari, after the wait for the API) ignores this, and Add photo,
      // which replaces this line, is that tap.
      input.current?.click()
    } catch (e) {
      if (!alive.current) return
      setConsentError(e instanceof ValidationError ? Object.values(e.fields).join(' ') : saveErrorMessage(e))
    } finally {
      if (alive.current) setRecording(false)
    }
  }

  // First in both shapes below, so React keeps the one element when the
  // consent line gives way to Add photo: the picker opened from Confirm
  // reports back to it.
  const fileInput = <input ref={input} type="file" accept="image/*" hidden onChange={picked} />

  if (!photoConsentStands(client.consent)) {
    return (
      <>
        {fileInput}
        <div className="add-note-photo__consent">
          {confirming ? (
            <>
              <p>{CONSENT_WORDING.photos.text.replace('[business]', businessName)}</p>
              {consentError && <p className="field-error" role="alert">{consentError}</p>}
              <div className="add-note-photo__actions">
                <button type="button" className="btn btn-secondary btn-compact" onClick={() => setConfirming(false)} disabled={recording}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary btn-compact" onClick={record} disabled={recording}>
                  {recording ? 'Recording…' : 'Confirm'}
                </button>
              </div>
            </>
          ) : (
            <>
              <p>{`Record that ${client.first_name} agreed to treatment photos first.`}</p>
              <div className="add-note-photo__actions">
                <button type="button" className="btn btn-secondary btn-compact" onClick={() => setConfirming(true)}>
                  Record agreement
                </button>
              </div>
            </>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {fileInput}
      <button type="button" className="btn btn-secondary btn-compact" onClick={() => input.current?.click()} disabled={reading}>
        {reading ? 'Adding…' : 'Add photo'}
      </button>
      {error && (
        <p className="field-error add-note-photo__error" role="alert">
          {error}
        </p>
      )}
    </>
  )
}
